"use client"
import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"

import PackageDetailsStep from "./steps/PackageDetailsStep"
import PackageContentStep from "./steps/PackageContentStep"
import PaymentOptionsStep, { PaymentOptionsStepRef } from "./steps/PaymentOptionsStep"
import FAQStep from "./steps/FAQStep"
import Modal from "../../components/common/Modal"
import { createPackage } from "../../api/packageService"
import { ArrowLeft, Check } from "lucide-react"

const STORAGE_KEY = 'currentPackage'

const CreatePackage: React.FC = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [packageData, setPackageData] = useState({
    title: "",
    description: "",
    coverImage: null as File | null,
    coverUrl: null as string | null,
    bannerImage: null as File | null,
    bannerUrl: null as string | null,
    courses: [] as string[],
    telegramId: [] as string[],
    requirements: "",
    targetAudience: "",
    learningOutcomes: "",
    priceDetails: [] as any[],
    faqs: [] as Array<{question: string, answer: string}>,
    status: "Draft"
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [, setHasChanges] = useState(false)
  const formRefs = [
    useRef<HTMLFormElement>(null),
    useRef<HTMLFormElement>(null),
    useRef<HTMLFormElement>(null),
    useRef<HTMLFormElement>(null)
  ]
  
  // Add ref for payment step
  const paymentStepRef = useRef<PaymentOptionsStepRef>(null)

  const steps = [
    { id: 1, title: "Package details", completed: currentStep > 1, current: currentStep === 1 },
    { id: 2, title: "Package content", completed: currentStep > 2, current: currentStep === 2 },
    { id: 3, title: "Payment options", completed: currentStep > 3, current: currentStep === 3 },
    { id: 4, title: "FAQs", completed: currentStep > 4, current: currentStep === 4 },
  ]

  // Load saved data on component mount
  useEffect(() => {
    const savedPackage = localStorage.getItem(STORAGE_KEY)
    if (savedPackage) {
      try {
        const parsedPackage = JSON.parse(savedPackage)
        setPackageData({
          ...parsedPackage,
          status: parsedPackage.status || "Draft"
        })
      } catch (e) {
        console.error("Failed to parse saved package data", e)
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [])

  // Save data to local storage when it changes
  useEffect(() => {
    if (Object.keys(packageData).length > 0) {
      // Don't save File objects to localStorage
      const { coverImage, bannerImage, ...dataToSave } = packageData
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
      setHasChanges(true)
    }
  }, [packageData])

  // Clear local storage when component unmounts or when package is successfully created
  useEffect(() => {
    return () => {
      // Only clear local storage if the package was not successfully created
      if (!showSuccessModal && !successMessage) {
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [showSuccessModal, successMessage])

  // Clear local storage when page is refreshed
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear local storage if the package was not successfully created
      if (!showSuccessModal && !successMessage) {
        localStorage.removeItem(STORAGE_KEY)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [showSuccessModal, successMessage])

  // const validateCurrentStep = () => {
  //   if (formRefs[currentStep - 1]?.current) {
  //     return formRefs[currentStep - 1].current!.checkValidity()
  //   }
  //   return true
  // }

  const moveToNextStep = () => {
    setCurrentStep(prev => prev + 1)
  }

  const moveToPrevStep = () => {
    setCurrentStep(prev => prev - 1)
  }

  // const handleNext = async () => {
  //   // Special handling for payment step (step 3)
  //   if (currentStep === 3) {
  //     try {
  //       if (paymentStepRef.current) {
  //         await paymentStepRef.current.savePaymentDetails()
  //       }
  //       moveToNextStep()
  //     } catch (error: any) {
  //       setError(error.message || "Failed to save payment details")
  //     }
  //     return
  //   }
    
  //   if (validateCurrentStep()) {
  //     setCurrentStep(prev => prev + 1)
  //   } else {
  //     formRefs[currentStep - 1].current!.reportValidity()
  //   }
  // }

  // const handleBack = () => {
  //   if (currentStep > 1) {
  //     moveToPrevStep()
  //   }
  // }

  const handleSaveDraft = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Validate required fields for draft
      if (!packageData.title || !packageData.description) {
        setError("Please fill in the basic package details (title and description) before saving as draft.")
        return
      }
      
      // Special handling for payment step when saving draft
      if (currentStep === 3 && paymentStepRef.current) {
        try {
          await paymentStepRef.current.savePaymentDetails()
        } catch (error) {
          // Continue even if payment validation fails for draft
          console.warn("Payment validation failed but continuing with draft save")
        }
      }
      
      // Call API with all data as draft
      const response = await createPackage({
        title: packageData.title,
        description: packageData.description,
        bannerFile: packageData.bannerImage!,
        themeFile: packageData.coverImage!,
        courseIds: packageData.courses,
        telegramId: packageData.telegramId,
        priceDetails: packageData.priceDetails.map((price: any) => ({
          period: price.period,
          actualPrice: price.actualPrice,
          price: price.price,
          gst: price.gst,
          totalprice: price.totalprice
        })),
        faqs: packageData.faqs.map(faq => ({ 
          question: faq.question, 
          answer: faq.answer 
        })),
        isDraft: true,
        status: "Draft"
      })
      
      if (response && (
        (response.status === 200 && response.data) || 
        (response.message && response.message.toLowerCase().includes('success')) ||
        (response.message === 'Package created successfully.')
      )) {
        localStorage.removeItem(STORAGE_KEY)
        setError(null)
        
        setSuccessMessage("Package saved as draft successfully!")
        
        setTimeout(() => {
          setSuccessMessage(null)
          navigate("/package", { replace: true })
        }, 2000)
      } else {
        if (response?.message === 'Package created successfully.') {
          localStorage.removeItem(STORAGE_KEY)
          setError(null)
          setSuccessMessage("Package saved as draft successfully!")
          
          setTimeout(() => {
            setSuccessMessage(null)
            navigate("/package", { replace: true })
          }, 2000)
        } else {
          setError(response?.message || "Failed to save package as draft")
        }
      }
    } catch (error: any) {
      console.error("Error saving package as draft:", error)
      setError(error.message || "Failed to save package as draft. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Validate all steps
      for (let i = 0; i < formRefs.length; i++) {
        if (formRefs[i]?.current && !formRefs[i].current!.checkValidity()) {
          setCurrentStep(i + 1)
          formRefs[i].current!.reportValidity()
          return
        }
      }
      
      // Additional validation for required fields
      if (!packageData.title || !packageData.description || !packageData.coverImage || !packageData.bannerImage) {
        setError("Please complete all required fields before publishing.")
        return
      }
      
      // Call API with all data as published
      const response = await createPackage({
        title: packageData.title,
        description: packageData.description,
        bannerFile: packageData.bannerImage!,
        themeFile: packageData.coverImage!,
        courseIds: packageData.courses,
        telegramId: packageData.telegramId,
        priceDetails: packageData.priceDetails.map((price: any) => ({
          period: price.period,
          actualPrice: price.actualPrice,
          price: price.price,
          gst: price.gst,
          totalprice: price.totalprice
        })),
        faqs: packageData.faqs.map(faq => ({ 
          question: faq.question, 
          answer: faq.answer 
        })),
        isDraft: false,
        status: "Published"
      })
      
      if (response && (
        (response.status === 200 && response.data) || 
        (response.message && response.message.toLowerCase().includes('success')) ||
        (response.message === 'Package created successfully.')
      )) {
        localStorage.removeItem(STORAGE_KEY)
        setError(null)
        
        setShowSuccessModal(true)
      } else {
        if (response?.message === 'Package created successfully.') {
          localStorage.removeItem(STORAGE_KEY)
          setError(null)
          setShowSuccessModal(true)
        } else {
          setError(response?.message || "Failed to publish package")
        }
      }
    } catch (error: any) {
      console.error("Error publishing package:", error)
      setError(error.message || "Failed to publish package. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleGoToDashboard = () => {
    setShowSuccessModal(false)
    navigate("/package", { replace: true })
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PackageDetailsStep
            data={packageData}
            onChange={setPackageData}
            formRef={formRefs[0]}
            onNext={moveToNextStep}
          />
        )
      case 2:
        return (
          <PackageContentStep
            data={packageData}
            onChange={setPackageData}
            formRef={formRefs[1]}
            onNext={moveToNextStep}
            onBack={moveToPrevStep}
            onSaveDraft={handleSaveDraft}
          />
        )
      case 3:
        return (
          <PaymentOptionsStep
            ref={paymentStepRef}
            data={packageData}
            onChange={setPackageData}
            onNext={moveToNextStep}
            onBack={moveToPrevStep}
            onSaveDraft={handleSaveDraft}
            loading={loading}
          />
        )
      case 4:
        return (
          <FAQStep
            data={packageData}
            onChange={setPackageData}
            formRef={formRefs[3]}
            onBack={moveToPrevStep}
            onNext={handlePublish}
            onSaveDraft={handleSaveDraft}
            loading={loading}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 py-2.5 shadow-xs">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/package")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <ArrowLeft className="mr-1" size={14} />
            <span className="hidden sm:inline text-xs">Go to dashboard</span>
            <span className="sm:hidden text-xs">Back</span>
          </button>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 shadow-xs">
        <div className="max-w-4xl mx-auto px-3 py-2.5">
          <div className="flex justify-between items-center mb-1.5">
            <h2 className="text-xs font-semibold text-gray-900">Progress</h2>
            <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
              {Math.round((currentStep / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="relative">
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold transition-all duration-300 ${
                      step.completed
                        ? "bg-green-500 text-white shadow-sm"
                        : step.current
                        ? "bg-blue-600 text-white shadow-sm ring-2 ring-blue-200"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {step.completed ? (
                      <Check className="w-2.5 h-2.5" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <span className={`mt-1 text-[9px] font-medium text-center max-w-12 leading-tight ${
                    step.current ? "text-blue-600" : "text-gray-500"
                  }`}>
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute top-2.5 left-2.5 right-2.5 h-0.5 bg-gray-200 -z-0">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500 ease-out"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>      
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-3">
        <div className="bg-white rounded-lg shadow-xs border border-gray-100 overflow-hidden">
          <div className="p-3">
            {/* Error Display */}
            {error && (
              <div className="mb-5 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                  <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-red-800">Error occurred</h4>
                  <p className="text-xs text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}
            
            {/* Success Display */}
            {successMessage && (
              <div className="mb-5 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start space-x-2">
                <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-green-800">Success!</h4>
                  <p className="text-xs text-green-700 mt-1">{successMessage}</p>
                </div>
              </div>
            )}
            
            {/* Step Content */}
            <div className="transform transition-all duration-300 ease-in-out">
              {renderCurrentStep()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {}}
        className="w-full max-w-xs"
      >
        <div className="text-center p-5">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 mb-3">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1.5">Package Published Successfully!</h3>
          <p className="text-gray-600 text-xs mb-4">
            Your package "{packageData.title}" has been published and is now available for students.
          </p>
          <button
            onClick={handleGoToDashboard}
            className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg font-semibold flex items-center justify-center space-x-1.5 text-sm"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Go to Dashboard</span>
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default CreatePackage